import { useEffect, useRef, useState } from "react";

function normalizeSegments(lines, segments) {
  if (segments?.length) {
    return segments.map((segment) =>
      typeof segment === "string"
        ? { text: segment, className: "" }
        : {
            text: segment.text,
            className: segment.className || "",
            breakAfter: Boolean(segment.breakAfter),
          }
    );
  }

  return (lines || []).map((line, lineIndex, arr) => ({
    text: line,
    className: "",
    breakAfter: lineIndex < arr.length - 1,
  }));
}

function BlurRevealText({ lines, segments, className = "", as: Tag = "h2" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const contentSegments = normalizeSegments(lines, segments);

  useEffect(() => {
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setReducedMotion(motionQuery.matches);
    updateMotion();
    motionQuery.addEventListener("change", updateMotion);
    return () => motionQuery.removeEventListener("change", updateMotion);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion) return;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.25, rootMargin: "0px 0px -48px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  if (reducedMotion) {
    return (
      <Tag className={className}>
        {contentSegments.map((segment, segmentIndex) => (
          <span key={segmentIndex} className={segment.className || undefined}>
            {segment.text}
            {segment.breakAfter && <br />}
          </span>
        ))}
      </Tag>
    );
  }

  let charIndex = 0;
  const staggerStep = 0.045;

  return (
    <Tag
      ref={ref}
      className={[
        "blur-reveal-text",
        visible && "blur-reveal-text--visible",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {contentSegments.map((segment, segmentIndex) => (
        <span
          className={["blur-reveal-segment", segment.className]
            .filter(Boolean)
            .join(" ")}
          key={`segment-${segmentIndex}`}
        >
          {[...segment.text].map((char, index) => {
            const delay = visible ? charIndex * staggerStep : 0;
            const key = `${segmentIndex}-${index}-${char}`;
            charIndex += 1;
            return (
              <span
                key={key}
                className="blur-reveal-char"
                style={{ transitionDelay: `${delay}s` }}
              >
                {char === " " ? "\u00a0" : char}
              </span>
            );
          })}
          {segment.breakAfter && <br />}
        </span>
      ))}
    </Tag>
  );
}

export default BlurRevealText;
